import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import { PPTViewer } from "../components/PPTViewer";
import { FullScreenPPTViewer } from "../components/FullScreenPPTViewer";
import { VideoSection } from "../components/VideoSection";
import AssessmentSection from "../components/AssessmentSection";
import { PDFViewer } from "../components/PDFViewer";
import { CompletionBadge } from "../components/CompletionBadge";
import ModuleFeedback from "../components/ModuleFeedback";
import { Warm } from "./Warm";

import AppShell from "../components/ui/AppShell";
import { Button, Card, EmptyState } from "../components/ui";
import { SwapIn } from "../components/ui/motion";
import { Check, Document, Module, Quiz, Slides, Video } from "../components/icons";

import { API_URL, getHeaders } from "../config";
import { useScreenTime } from "../context/ScreenTimeContext";
import { useContentProgress } from "../hooks/useContentProgress";
import "../assets/styles/button.css";

/**
 * Resolve a one-shot "openTarget" (set by My Learning when opening an assigned
 * item) to a { catIdx, itemIdx } within the loaded categories. Consumes the
 * target from localStorage and returns null when there's nothing to deep-link
 * (or the content isn't in this section), so callers fall back to defaults.
 */
const resolveOpenTarget = (categories) => {
  let target;
  try {
    target = JSON.parse(localStorage.getItem("openTarget"));
  } catch {
    target = null;
  }
  if (!target?.contentId) return null;
  localStorage.removeItem("openTarget");

  const id = String(target.contentId);
  if (target.contentType === "ppt-module") {
    const catIdx = categories.findIndex((c) => String(c._id) === id);
    return catIdx >= 0 ? { catIdx, itemIdx: 0 } : null;
  }
  if (target.contentType === "ppt-item") {
    const catIdx = categories.findIndex((c) =>
      (c.items || []).some((it) => String(it._id) === id)
    );
    if (catIdx < 0) return null;
    const itemIdx = categories[catIdx].items.findIndex(
      (it) => String(it._id) === id
    );
    return { catIdx, itemIdx: Math.max(itemIdx, 0) };
  }
  if (target.contentType === "video") {
    const pptItemId = target.pptItemId ? String(target.pptItemId) : null;
    if (!pptItemId) return null;
    const catIdx = categories.findIndex((c) =>
      (c.items || []).some((it) => String(it._id) === pptItemId)
    );
    if (catIdx < 0) return null;
    const itemIdx = categories[catIdx].items.findIndex(
      (it) => String(it._id) === pptItemId
    );
    return { catIdx, itemIdx: Math.max(itemIdx, 0), forceSection: "Video" };
  }
  if (target.contentType === "assessment") {
    const pptItemId = target.pptItemId ? String(target.pptItemId) : null;
    if (!pptItemId) return null;
    const catIdx = categories.findIndex((c) =>
      (c.items || []).some((it) => String(it._id) === pptItemId)
    );
    if (catIdx < 0) return null;
    const itemIdx = categories[catIdx].items.findIndex(
      (it) => String(it._id) === pptItemId
    );
    return { catIdx, itemIdx: Math.max(itemIdx, 0), forceSection: "Warmup" };
  }
  return null;
};

/**
 * The four ways a lesson can be consumed. Previously a floating pill bar at the
 * bottom of the viewport, which meant the learner had to look away from the
 * content to find out what else the lesson contained. As tiles above the stage
 * they're an inventory: what exists, which one you're in, what's finished.
 */
const MODES = {
  PPT: { label: "Slides", icon: Slides, sub: "Presentation" },
  Video: { label: "Video", icon: Video, sub: "Recorded lesson" },
  PDF: { label: "Document", icon: Document, sub: "Reference PDF" },
  Warmup: { label: "Warm up", icon: Quiz, sub: "Practice questions" },
};

export const Home = () => {
  const [categories, setCategories] = useState([]);
  const [selectedPpt, setSelectedPpt] = useState("");
  const [selectedPPTIndex, setPPTIndex] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const [items, setItems] = useState({});
  const [isFullScreen, setFullScreen] = useState(false);
  const [isUpdated, setUpdated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const section = localStorage.getItem("section");

  const { startPPTTracking, stopPPTTracking } = useScreenTime();

  const [selectedSection, setSelectedSection] = useState("PPT");
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const isDeepLinking = useRef(false);
  // True only while the feedback dialog is standing between the learner and a
  // module they asked to switch to.
  const pendingSwitch = useRef(false);

  // Completion evidence for the lesson on screen. The legacy PPTHistory time-log
  // still records ATTENDANCE ("they opened it"); this records what the learner
  // actually did — foreground seconds and pages reached — and the SERVER decides
  // completion from it. Reading content only; Video and Warmup track their own.
  const { reportPage, status: progressStatus } = useContentProgress({
    contentType: "ppt-item",
    contentId: selectedPpt?._id,
    // Only track when there is genuinely something to read. A lesson with
    // neither a deck nor a PDF renders an empty viewer, and idling in front of
    // nothing must not earn credit.
    enabled:
      ((selectedSection === "PPT" && !!selectedPpt?.ppt) ||
        (selectedSection === "PDF" && !!selectedPpt?.pdf)) &&
      !!selectedPpt?._id,
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("savedLayouts");
    navigate("/");
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/PPT?section=${section}`, {
          headers: getHeaders().headers,
        });
        setCategories(data?.data || []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) handleLogout();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isUpdated) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/PPT?section=${section}`, {
          headers: getHeaders().headers,
        });
        setCategories(data?.data || []);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [isUpdated]);

  useEffect(() => {
    if (!categories.length) return;
    setUpdated(false);

    // Honor a deep-link target set by "My Learning" when opening an assigned
    // item, so we land on the EXACT module/lesson instead of the section's
    // first one. Consumed once so normal navigation is unaffected.
    const target = resolveOpenTarget(categories);
    if (target) {
      isDeepLinking.current = true;
      setOpenCategory(target.catIdx);
      setPPTIndex(target.itemIdx);
      setSelectedPpt(categories[target.catIdx].items[target.itemIdx]);
      if (target.forceSection) setSelectedSection(target.forceSection);
      return;
    }

    if (openCategory === null) setOpenCategory(0);
    if (selectedPPTIndex === null) setPPTIndex(0);
    if (!selectedPpt && categories[0]?.items?.length) {
      setSelectedPpt(categories[0].items[0]);
    }
  }, [categories]);

  useEffect(() => {
    if (categories.length && openCategory >= 0 && selectedPPTIndex >= 0) {
      setSelectedPpt(categories[openCategory]?.items[selectedPPTIndex]);
    }
  }, [selectedPPTIndex, openCategory, categories]);

  /**
   * The modes this lesson actually offers. Slides and the PDF appear only when
   * the lesson carries them, so a tile never opens an empty stage.
   */
  const availableModes = useMemo(() => {
    const modes = [];
    if (selectedPpt?.ppt) modes.push("PPT");
    modes.push("Video");
    if (selectedPpt?.pdf) modes.push("PDF");
    modes.push("Warmup");
    return modes;
  }, [selectedPpt]);

  useEffect(() => {
    if (!selectedPpt) return;
    // A deep link already chose the mode; anything else starts at the first
    // available one so the learner never lands on a blank stage.
    if (isDeepLinking.current) {
      isDeepLinking.current = false;
      return;
    }
    setSelectedSection(availableModes[0]);
  }, [selectedPpt]);

  useEffect(() => {
    if (!selectedPpt) return;
    const category = categories[openCategory];
    if (!category) return;
    setItems({ categoryName: category.title, items: category.items });
  }, [selectedPpt]);

  const handleItemClick = (pptItem, index) => {
    setSelectedPpt(pptItem);
    setPPTIndex(index);
  };

  const handleFullScreen = () => {
    const elem = document.documentElement;
    const inFullScreen =
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;

    if (!inFullScreen) {
      const request =
        elem.requestFullscreen ||
        elem.mozRequestFullScreen ||
        elem.webkitRequestFullscreen ||
        elem.msRequestFullscreen;
      request?.call(elem);
    } else {
      const exit =
        document.exitFullscreen ||
        document.mozCancelFullScreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      exit?.call(document);
    }
  };

  useEffect(() => {
    const onFullScreenChange = () => {
      const inFullScreen =
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;

      if (inFullScreen) {
        startPPTTracking();
        setFullScreen(true);
      } else {
        stopPPTTracking(selectedPpt.title);
        setFullScreen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape" && isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
      }
    };

    document.addEventListener("fullscreenchange", onFullScreenChange);
    document.addEventListener("webkitfullscreenchange", onFullScreenChange);
    document.addEventListener("mozfullscreenchange", onFullScreenChange);
    document.addEventListener("MSFullscreenChange", onFullScreenChange);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullScreenChange);
      document.removeEventListener("mozfullscreenchange", onFullScreenChange);
      document.removeEventListener("MSFullscreenChange", onFullScreenChange);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isFullScreen]);

  const selectPrevious = (index) => {
    if (index) {
      setSelectedPpt(categories[openCategory].items[index - 1]);
      setPPTIndex(index - 1);
    }
  };

  const selectNext = (index) => {
    const total = categories[openCategory].items.length;
    const next = index + 1 === total ? 0 : index + 1;
    setSelectedPpt(categories[openCategory].items[next]);
    setPPTIndex(next);
  };

  /**
   * Switching module asks for feedback on the one being left. Only prompts when
   * the learner is genuinely moving away — re-clicking the open module is a
   * no-op, not a reason to interrupt.
   */
  const handleSwitchModule = (index) => {
    if (typeof index === "number" && openCategory !== index) {
      setCategoryIndex(index);
      pendingSwitch.current = true;
      setFeedbackModalOpen(true);
    }
  };

  /**
   * Dismissing the dialog must only move the learner when the dialog was
   * *caused* by a module switch. Opened deliberately from "Give feedback", it
   * closes and leaves them exactly where they were.
   */
  const handleSwitch = () => {
    if (pendingSwitch.current) {
      pendingSwitch.current = false;
      setOpenCategory(categoryIndex === openCategory ? null : categoryIndex);
      setSelectedSection("PPT");
    }
    setFeedbackModalOpen(false);
  };

  if (isFullScreen && selectedSection === "PPT") {
    return (
      <FullScreenPPTViewer
        ppt={selectedPpt}
        handleFullScreen={handleFullScreen}
        isFullScreen={isFullScreen}
        setCategories={setCategories}
        selectedPPTIndex={selectedPPTIndex}
        selectedCategoryIndex={openCategory}
      />
    );
  }

  const isWarmUpMode = selectedSection === "generalWarmup";

  return (
    <AppShell crumb={section || "Section"} active="section" wide>
      {isFeedbackModalOpen && (
        <ModuleFeedback
          open={isFeedbackModalOpen}
          show={setFeedbackModalOpen}
          handleAgree={handleSwitch}
          module={categories[openCategory]}
          content="How was this module? Your rating and comments go to the training team."
        />
      )}

      <div className="ds-workspace">
        <Sidebar
          categories={categories}
          onItemClick={handleItemClick}
          openCategory={openCategory}
          setOpenCategory={setOpenCategory}
          selectedPPT={selectedPpt}
          selectedPPTIndex={selectedPPTIndex}
          selectedSection={selectedSection}
          currentSection={setSelectedSection}
          handleSwitchModule={handleSwitchModule}
        />

        <div>
          {isWarmUpMode ? (
            <Warm />
          ) : loading ? (
            <Card>
              <EmptyState icon={Module} text="Loading this section…" />
            </Card>
          ) : !selectedPpt ? (
            <Card>
              <EmptyState
                icon={Module}
                text={`No modules have been published in ${section || "this section"} yet.`}
              />
            </Card>
          ) : (
            <>
              <div className="ds-stage__head">
                <div className="ds-grow">
                  <h1 className="ds-h1 ds-truncate">{selectedPpt.title}</h1>
                  {items.categoryName && (
                    <p className="ds-sub">{items.categoryName}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFeedbackModalOpen(true)}
                >
                  Give feedback
                </Button>
              </div>

              <div className="ds-modes">
                {availableModes.map((mode) => {
                  const meta = MODES[mode];
                  const ModeIcon = meta.icon;
                  const isActive = selectedSection === mode;
                  const isDone =
                    mode === "PPT" && progressStatus?.completed === true;
                  return (
                    <button
                      key={mode}
                      type="button"
                      className={`ds-mode${isActive ? " ds-mode--active" : ""}`}
                      onClick={() => setSelectedSection(mode)}
                    >
                      {isDone && (
                        <span className="ds-mode__check">
                          <Check size={11} />
                        </span>
                      )}
                      <span className="ds-mode__icon">
                        <ModeIcon size={21} />
                      </span>
                      <span className="ds-mode__label">{meta.label}</span>
                      <span className="ds-mode__sub">{meta.sub}</span>
                    </button>
                  );
                })}
              </div>

              <SwapIn swapKey={`${selectedPpt._id}-${selectedSection}`} className="ds-stage">
                {selectedSection === "PPT" && (
                  <PPTViewer
                    ppt={selectedPpt}
                    items={items}
                    selectNext={selectNext}
                    selectPrevious={selectPrevious}
                    pptIndex={selectedPPTIndex}
                    onItemClick={handleItemClick}
                    handleFullScreen={handleFullScreen}
                    isFullScreen={isFullScreen}
                    setUpdated={setUpdated}
                    setCategories={setCategories}
                    selectedPPTIndex={selectedPPTIndex}
                    selectedCategoryIndex={openCategory}
                    selectedSection={selectedSection}
                    setSelectedSection={setSelectedSection}
                  />
                )}

                {selectedSection === "Video" && (
                  <VideoSection
                    ppt={selectedPpt}
                    items={items}
                    pptIndex={selectedPPTIndex}
                    onItemClick={handleItemClick}
                    selectedPPTIndex={selectedPPTIndex}
                    selectedSection={selectedSection}
                    setSelectedSection={setSelectedSection}
                  />
                )}

                {selectedSection === "Warmup" && (
                  <AssessmentSection selectedModule={selectedPpt} />
                )}

                {selectedSection === "PDF" && (
                  <PDFViewer pdf={selectedPpt.pdf} onPage={reportPage} />
                )}
              </SwapIn>
            </>
          )}
        </div>
      </div>

      {(selectedSection === "PPT" || selectedSection === "PDF") && (
        <CompletionBadge status={progressStatus} />
      )}
    </AppShell>
  );
};

export default Home;
